/**
 * KMMO kit extras for the Hero / Enhance screens (group B). Kept out of the kit's own files:
 *   metaFrame   screenFrame wired to the profile store: the 3 top-bar currencies (gold, Fangsteel,
 *               Altar Sigil) update live, plus the settings gear and "to the lobby".
 *   infoSheet   the (i) "How this works" sheet (short paragraphs).
 *   gemIcon     a gem as a slot icon.
 */
import type { GemColor } from '@keystone/progression';
import type { MetaScreenContext } from '../context';
import type { ProfileStore } from '../profileStore';
import { el } from '../ui/dom';
import { gemGlyphSvg } from '../ui/glyphs';
import { toggleSettingsPanel } from '../../hud/settingsPanel';
import { type ScreenFrame, type ScreenFrameOptions, kmModal, screenFrame } from './components';
import { RESOURCE_ICON, iconUrl } from './icons';

export interface MetaFrame extends ScreenFrame {
  dispose(): void;
}

/** The three top-bar currencies of a profile (art direction: never more than 3). */
export function topCurrencies(store: ProfileStore): NonNullable<ScreenFrameOptions['currencies']> {
  const p = store.profile;
  return [
    { id: 'gold', icon: 'gold', value: p.currencies.gold, title: 'Gold' },
    { id: 'fangsteel', icon: iconUrl(RESOURCE_ICON['fangsteel']!, true), value: p.wallet.fangsteel, title: 'Fangsteel' },
    { id: 'sigils', icon: iconUrl(RESOURCE_ICON['sigils']!, true), value: p.wallet.sigils, title: 'Altar Sigils' },
  ];
}

/**
 * Screen frame for a meta screen: back arrow (router.back), title, optional (i), the 3 live
 * currencies, then ⚙ (graphics settings) and ⇥ (to the lobby).
 */
export function metaFrame(ctx: MetaScreenContext, o: { title: string; info?: () => void; className?: string }): MetaFrame {
  const f = screenFrame({
    title: o.title,
    onBack: () => ctx.router.back(),
    onInfo: o.info,
    className: o.className,
    currencies: topCurrencies(ctx.store),
    utilities: [
      { icon: 'settings', title: 'Settings', onClick: () => toggleSettingsPanel() },
      ...(ctx.router.isOverlay ? [] : [{ icon: 'exit' as const, title: 'Back to the world', onClick: () => ctx.router.reset() }]),
    ],
  });
  const unsub = ctx.store.subscribe((p) => {
    f.currencies?.set('gold', p.currencies.gold);
    f.currencies?.set('fangsteel', p.wallet.fangsteel);
    f.currencies?.set('sigils', p.wallet.sigils);
  });
  return { ...f, dispose: unsub };
}

/** "How this works" sheet: a few short paragraphs (and optional rows of label · value). */
export function infoSheet(title: string, paras: readonly string[], rows: readonly [string, string][] = []): void {
  const m = kmModal(title, { wide: true });
  for (const t of paras) el('p', 'km-modal-text kx-info-p', m.body, t);
  if (rows.length) {
    const dl = el('dl', 'km-stats kx-info-rows', m.body);
    for (const [k, v] of rows) {
      const r = el('div', 'km-stat', dl);
      el('dt', '', r, k);
      el('dd', 'km-num', r, v);
    }
  }
}

/** A gem glyph sized for a kit slot. */
export function gemIcon(color: GemColor, rank: number): SVGSVGElement {
  const g = gemGlyphSvg(color, rank);
  g.classList.add('km-glyph', 'kx-gem');
  return g;
}
